import { OverlayModule } from '@angular/cdk/overlay';
import { DatePipe, NgClass, NgStyle } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslatePipe } from '@ngx-translate/core';

import { RtudataStore } from '../rtudata.store';

@Component({
  imports: [
    MatCardModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatIcon,
    OverlayModule,
    DatePipe,
    NgClass,
    NgStyle,
    TranslatePipe,
  ],
  selector: 'norupe-date-selection',
  template: ` <button
      #trigger="cdkOverlayOrigin"
      cdkOverlayOrigin
      type="button"
      class="flex cursor-pointer rounded-sm border border-gray-200"
      (click)="dateSelectorOpen = !dateSelectorOpen"
    >
      <span class="inline-block p-1 px-2">
        {{
          RtudataStore.selectedScan()?.session_start
            | date: 'd MMM y - H:mm:ss '
        }}
      </span>
      <span class="flex items-center bg-gray-200">
        <mat-icon>arrow_drop_down</mat-icon>
      </span>
    </button>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOpen]="dateSelectorOpen"
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayPositions]="[
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
      ]"
    >
      <div class="shadow-xs flex border border-gray-300 bg-white">
        <div class="p-2">
          <div class="flex space-x-2">
            @if (RtudataStore.scanDates()) {
              <mat-card class="demo-inline-calendar-card">
                <mat-calendar
                  [dateFilter]="myFilter"
                  [selected]="RtudataStore.selectedDate()"
                  [startAt]="starAt(1)"
                  (selectedChange)="onSelectedDateChange($event)"
                />
              </mat-card>
              <mat-card class="demo-inline-calendar-card">
                <mat-calendar
                  [dateFilter]="myFilter"
                  [selected]="RtudataStore.selectedDate()"
                  (selectedChange)="onSelectedDateChange($event)"
                />
              </mat-card>
            }
          </div>
          <div class="flex items-center justify-between">
            <div>
              <fieldset class="flex space-x-2" aria-label="Choose a scan time">
                @let scanByDate = RtudataStore.scanByDate();
                @for (scan of scanByDate; track $index) {
                  <div class="flex flex-col gap-y-1">
                    <label
                      class="focus:outline-hidden cursor-pointer items-center justify-center rounded-md border px-3 py-3 text-sm font-semibold uppercase ring-inset sm:flex-1"
                      [ngClass]="{
                        'bg-indigo-600 text-white ring-0 hover:bg-indigo-500':
                          scan.time === RtudataStore.selectedTime(),
                      }"
                    >
                      <input
                        type="radio"
                        class="sr-only"
                        name="scan-time"
                        [value]="scan.time"
                        (change)="onTimeChange($event)"
                      />
                      <span>{{ scan.time }}</span>
                    </label>
                    <div
                      class="grow-1 flex items-center justify-center space-y-1 align-middle"
                    >
                      @if (scan.partial_scan) {
                        <mat-icon
                          [matTooltip]="'MONITORING.partial_scan' | translate"
                          [ngStyle]="{ color: 'grey' }"
                          >incomplete_circle</mat-icon
                        >
                      }
                      @if (scan.scheduled_scan) {
                        <mat-icon
                          [matTooltip]="'MONITORING.scheduled_scan' | translate"
                          [ngStyle]="{ color: 'grey' }"
                          >event</mat-icon
                        >
                      }
                    </div>
                  </div>
                }
              </fieldset>
            </div>

            <div class="flex flex-col space-y-2 p-5">
              <button
                type="button"
                class="shadow-xs cursor-pointer rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                (click)="updateSelectedScan()"
              >
                {{ 'GENERAL.apply' | translate }}
              </button>
              <button
                type="button"
                class="shadow-xs cursor-pointer rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                (click)="dateSelectorOpen = false"
              >
                {{ 'GENERAL.cancel' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ng-template>`,
})
export class DateSelectionComponent {
  readonly RtudataStore = inject(RtudataStore);

  dateSelectorOpen = false;

  constructor() {
    effect(() => {
      this.RtudataStore.setSelectedDate(
        new Date(this.RtudataStore.selectedScan()?.session_start ?? ''),
      );

      this.RtudataStore.setSelectedTime(
        this.RtudataStore.selectedScan()
          ? new Date(
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              this.RtudataStore.selectedScan()!.session_start,
            ).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            })
          : null,
      );
    });
  }

  onTimeChange(event: Event) {
    this.RtudataStore.setSelectedTime((event.target as HTMLInputElement).value);
  }

  onSelectedDateChange(date: Date | null) {
    if (!date) {
      return;
    }
    this.RtudataStore.setSelectedDate(date);
  }

  updateSelectedScan() {
    const selectedTime = this.RtudataStore.selectedTime();
    const selectedDate = this.RtudataStore.selectedDate();

    const scanByDate = this.RtudataStore.scanByDate();
    const matchingScan = scanByDate.find((scan) => {
      const scanDate = new Date(scan.session_start);
      const dateMatch =
        scanDate.getFullYear() === selectedDate?.getFullYear() &&
        scanDate.getMonth() === selectedDate.getMonth() &&
        scanDate.getDate() === selectedDate.getDate();
      const timeMatch =
        scanDate.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
        }) === selectedTime;
      return dateMatch && timeMatch;
    });

    if (matchingScan) {
      this.RtudataStore.setSelected(matchingScan.id);
    }
    this.dateSelectorOpen = false;
    this.RtudataStore.loadSession(this.RtudataStore.selectedScanId() ?? 0);
  }

  myFilter = (d: Date | null): boolean => {
    // Check if the date exists in the RtudataStore entities
    const dates = this.RtudataStore.scanDates();

    if (
      dates.some((date) => {
        const entityDate = new Date(date);
        return (
          entityDate.getFullYear() === (d ?? new Date()).getFullYear() &&
          entityDate.getMonth() === (d ?? new Date()).getMonth() &&
          entityDate.getDate() === (d ?? new Date()).getDate()
        );
      })
    ) {
      return true;
    }

    return false;
  };

  starAt(previousMonth?: number) {
    const today = new Date();
    const month = today.getMonth() - (previousMonth ?? 0); //previous month
    const year = today.getUTCFullYear();
    const day = today.getDay();

    return new Date(year, month, day);
  }
}
